import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SupportNavbar from "../../components/support-components/SupportNavbar";
import SupportTopbar from "../../components/support-components/SupportTopbar";
import API_BASE_URL from "../../config";
import "../SalesPages.css";

function SupportTickets() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");

  const loadTickets = async (statusOverride = null) => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");
      const params = new URLSearchParams();

      if (statusOverride && statusOverride !== "All") {
        params.set("status", statusOverride);
      }

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/support-tickets/admin${
        queryString ? `?${queryString}` : ""
      }`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to load support tickets");
      }

      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load support tickets");
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const status = searchParams.get("status") || "All";
    setStatusFilter(status);
    loadTickets(status);
  }, [searchParams]);

  const filteredTickets = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    return tickets.filter((ticket) => {
      const matchesSearch =
        !term ||
        [
          ticket.id,
          ticket.subject,
          ticket.category,
          ticket.priority,
          ticket.status,
          ticket.submitted_by_name,
          ticket.submitted_by_email,
          ticket.tenant_type,
          ticket.tenant_id,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));

      const matchesPriority =
        priorityFilter === "All" || ticket.priority === priorityFilter;

      const matchesCategory =
        categoryFilter === "All" || ticket.category === categoryFilter;

      return matchesSearch && matchesPriority && matchesCategory;
    });
  }, [tickets, searchTerm, priorityFilter, categoryFilter]);

  const updateStatusFilter = (value) => {
    setStatusFilter(value);

    if (value === "All") {
      setSearchParams({});
      return;
    }

    setSearchParams({ status: value });
  };

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString();
  };

  const statusClass = (value) =>
    `sales-chip ${String(value || "").toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <>
      <SupportNavbar />
      <SupportTopbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM Support</p>
            <h1 className="sales-title">Support Tickets</h1>
            <p className="sales-subtitle">
              View, filter, and manage support requests submitted by Agent Book CRM users.
            </p>
          </div>

          <div className="sales-header-actions">
            <button
              className="sales-secondary-btn"
              onClick={() => loadTickets(statusFilter)}
            >
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="sales-section-card">
            {error}
          </div>
        )}

        <div className="sales-toolbar-card">
          <input
            type="text"
            className="sales-search-input"
            placeholder="Search by ticket ID, subject, user, email, status, or tenant..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />

          <div className="sales-filter-row">
            <select
              className="sales-search-input"
              value={statusFilter}
              onChange={(event) => updateStatusFilter(event.target.value)}
            >
              <option value="All">Active Queue</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Waiting on Customer">Waiting on Customer</option>
              <option value="Resolved">Resolved</option>
              <option value="Closed">Closed</option>
            </select>

            <select
              className="sales-search-input"
              value={priorityFilter}
              onChange={(event) => setPriorityFilter(event.target.value)}
            >
              <option value="All">All Priorities</option>
              <option value="Urgent">Urgent</option>
              <option value="High">High</option>
              <option value="Normal">Normal</option>
              <option value="Low">Low</option>
            </select>

            <select
              className="sales-search-input"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
            >
              <option value="All">All Categories</option>
              <option value="Issue / Bug">Issue / Bug</option>
              <option value="Question">Question</option>
              <option value="Recommendation">Recommendation</option>
              <option value="Feature Request">Feature Request</option>
            </select>
          </div>
        </div>

        <div className="sales-table-card">
          <div className="sales-table-header">
            <div>
              <h2>
                {statusFilter === "Closed" ? "Closed Tickets" : "Ticket Queue"}
              </h2>
              <p>
                {filteredTickets.length} ticket{filteredTickets.length === 1 ? "" : "s"}
              </p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Submitted By</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Created</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="sales-empty-cell">
                      Loading support tickets...
                    </td>
                  </tr>
                ) : filteredTickets.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="sales-empty-cell">
                      No support tickets found.
                    </td>
                  </tr>
                ) : (
                  filteredTickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className="sales-click-row"
                      onClick={() => navigate(`/support-admin/tickets/${ticket.id}`)}
                    >
                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">#{ticket.id} {ticket.subject}</div>
                          <div className="sales-secondary">
                            {ticket.tenant_type} #{ticket.tenant_id}
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="sales-primary-cell">
                          <div className="sales-name">
                            {ticket.submitted_by_name || "—"}
                          </div>
                          <div className="sales-secondary">
                            {ticket.submitted_by_email || "—"}
                          </div>
                        </div>
                      </td>

                      <td>{ticket.category || "—"}</td>
                      <td>{ticket.priority || "—"}</td>
                      <td>
                        <span className={statusClass(ticket.status)}>
                          {ticket.status || "—"}
                        </span>
                      </td>
                      <td>{ticket.assigned_to_name || "Unassigned"}</td>
                      <td>{formatDate(ticket.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default SupportTickets;